import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { confirmPasswordReset } from "../api/auth";
import AuthShell from "../components/auth/AuthShell";

function resetTokenFromSearch(search) {
  const params = new URLSearchParams(search);
  return (
    params.get("token") ||
    params.get("reset_token") ||
    params.get("code") ||
    ""
  ).trim();
}

function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = resetTokenFromSearch(location.search);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusColor, setStatusColor] = useState("text.secondary");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!token) return;
    if (!newPassword || newPassword !== confirmPassword) {
      setStatusMessage("Passwords do not match.");
      setStatusColor("error.main");
      return;
    }
    setLoading(true);
    setStatusMessage("");
    try {
      await confirmPasswordReset(token, newPassword);
      setDone(true);
      setStatusMessage("Password reset. Redirecting to login…");
      setStatusColor("success.main");
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    } catch (error) {
      setStatusMessage(error.message || "Reset link is invalid or expired.");
      setStatusColor("error.main");
    } finally {
      setLoading(false);
    }
  }

  const statusSeverity = statusColor === "success.main" ? "success" : "error";

  return (
    <AuthShell
      background="radial-gradient(circle at top right, rgba(217, 168, 107, 0.2), transparent 32%), radial-gradient(circle at bottom left, rgba(95, 128, 178, 0.16), transparent 34%), linear-gradient(155deg, #f6efe5 0%, #edf4fb 100%)"
      heroBackground="linear-gradient(180deg, rgba(247, 239, 228, 0.98) 0%, rgba(244, 248, 252, 0.98) 100%)"
      formBackground="linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(250,252,255,0.98) 100%)"
      heroTone="light"
      heroBadge="Secure reset"
      heroTitle="Set a new password and keep moving."
      heroBody="This is a quick reset step so you can get back into your account and back to the work that matters."
      heroNote="Use a password you’ll remember and that you haven’t used elsewhere."
      eyebrow="Reset password"
      title="Choose a new password."
      body="Confirm the new password once, then we’ll send you back to login."
      maxWidth={980}
      reverseOnDesktop
    >
      {!token ? (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          This reset link is missing or invalid.
        </Alert>
      ) : (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
                New password
              </Typography>
              <TextField
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                fullWidth
                required
                disabled={done}
                placeholder="Create a new password"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                }}
              />
            </Stack>

            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
                Confirm password
              </Typography>
              <TextField
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                fullWidth
                required
                disabled={done}
                placeholder="Enter it again"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                }}
              />
            </Stack>

            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              disabled={loading || done || !newPassword || !confirmPassword}
              sx={{
                borderRadius: 999,
                py: 1.35,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 16px 28px rgba(27, 64, 111, 0.22)",
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: "inherit", mr: 1 }} /> : null}
              {loading ? "Resetting password..." : "Reset password"}
            </Button>
          </Stack>
        </form>
      )}

      {statusMessage ? (
        <Alert severity={statusSeverity} sx={{ borderRadius: 3 }}>
          {statusMessage}
        </Alert>
      ) : null}

      <Box
        sx={{
          pt: 2,
          borderTop: "1px solid #e4eaf1",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: "#5f6978" }}>
          Need to start over?
        </Typography>
        <Button
          component={RouterLink}
          to="/login"
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 2,
            fontWeight: 700,
          }}
        >
          Back to login
        </Button>
      </Box>
    </AuthShell>
  );
}

export default ResetPasswordPage;
